import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { changeSearchTerms } from '../../data/jobs/jobActions';
import { createJobSelector } from '../../data/jobs/jobSelectors';
import { createOpenWebJobSelector } from '../../data/owp/owpSelectors';
import { createIsLoading,
         createDidError, } from '../../data/requests/requestsSelectors';
import {createAgency} from "../../data/agencies/agenciesActions";
import { Loading, Error } from '../static/Indicators';
import { Button } from '../static/Elements';
import Icon from '../static/Icons';
import {withAlert} from "react-alert";
import { Collapse } from 'react-collapse';
import renderHTML from 'react-render-html';
import createLogger from '../../utils/createLogger';
import compose from "recompose/compose";
import {updateTitle} from "../../data/titles/titleActions";

const DEBUG = true;
const debug = createLogger(DEBUG, `JobSearch.js`);


class JobSearch extends React.Component {

  static role = `search`;
  static dependencies = [
    `GET_JOB`,
    `PUT_JOB`,
    `GET_OPENWEB_JOB`,
    `PUT_OPENWEB_JOB`,
    `POST_AGENCY`,
  ];

  static propTypes = {
    id: PropTypes.string.isRequired,
    job: PropTypes.object,
    didError: PropTypes.bool,
    isLoading: PropTypes.bool,
    changeSearchTerms: PropTypes.func,
    selectable: PropTypes.bool,
    onClickPromote: PropTypes.func,
    deselect: PropTypes.func,
  };
  /* ------------ render methods ------------ */

  render() {
    const {
      id, job,
      isLoading,
      didError,
      selectable,
      deselect,
      openModal,
      selectMatch,
      createAgency,
    } = this.props;

    debug.enter(`render`);
    debug.log(`  id: ${id}`);
    debug.log(`  didError: ${didError}`);
    debug.log(`  isLoading: ${isLoading}`);

    if (job === undefined) {
      return (
        <aside className="job-search">
          <Error message="Job search results not available"/>
        </aside>
      )
    }

    if (isLoading || didError) {
      return (
        <article className="details">
          { didError
            ? <Error message="Error loading job post details"/>
            : <Loading/> }
        </article>
      )
    }

    const {
      search_data,
      max_ranking
    } = job;

    debug.log(`  results: `, job.results);

    let searchTerms = null;
    if (job.results && job.results[0] &&
        job.results[0].strategy === `__manual__`) {
      if (typeof terms === `string`)
        searchTerms = decodeURIComponent(job.results[0].terms.split(`+`).join(` `));
      else if (Array.isArray(job.results[0].terms))
        searchTerms = decodeURIComponent(job.results[0].terms.join(` `));
      else
        searchTerms = `Search terms unavailable`;
    }

    return (
      <aside className="job-search">
        <React.Fragment>
          <h4 className={'subhead-padding'}>Results</h4>
        </React.Fragment>
        {
          !!searchTerms && (
            <div className="terms">
              <h6>Search:</h6>
              <p>{ renderHTML(searchTerms) }</p>
            </div>
          )
        }

        <section>
          { isLoading
            ? <Loading/>
            : didError
              ? <Error message="Error searching for job"/>
              : !job.results || job.results.length < 1
                ? <Error message="Search results not available"/>
                : job.results.map((result, i) => <SearchResult id={`job-${id}-result-${i}`}
                                                               joboid={id} search_terms={job['search_terms']}
                                                               key={i} {...result} selectable={selectable}
                                                               search_data={search_data} max_ranking={max_ranking}
                                                               onClickPromote={openModal} selectMatch={selectMatch}
                                                               deselect={deselect} exclude={createAgency} />)
          }
        </section>
      </aside>
    )
  }
}

class SearchResult extends React.Component {

  static propTypes = {
    id: PropTypes.string.isRequired,
    info: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    header: PropTypes.string.isRequired,
    search_data: PropTypes.object,
    domain: PropTypes.string,
    strategy: PropTypes.string,
    related: PropTypes.array,
    selectable: PropTypes.bool,
    onClickPromote: PropTypes.func,
    joboid: PropTypes.string,
    deselect: PropTypes.func,
  };

  constructor (props) {
    super (props);

    this.state = {
      relatedIsOpen: false,
      stratInfoIsOpen: false,
      selectIsVisible: true,
      rankInfoIsOpen: false,
    }
  }

  toggleRelatedResults = () => this.setState ({relatedIsOpen: !this.state.relatedIsOpen});
  toggleStrategyInfo = () => this.setState ({stratInfoIsOpen: !this.state.stratInfoIsOpen});
  toggleRankInfo = () => this.setState ({rankInfoIsOpen: !this.state.rankInfoIsOpen});

  renderRelatedResults = () => {
    const {toggleRelatedResults} = this;
    const {id, related} = this.props;
    const {relatedIsOpen} = this.state;

    const config = {
      key: `${id}-related`,
      className: classnames ({
        'related-results': true,
        'open': relatedIsOpen,
      }),
    };

    return !!related && related.length > 0 ? (
      <div {...config}>
        <Button type="disclose-related"
                onClick={toggleRelatedResults}>
          Related <Icon.Caret isOpen={relatedIsOpen}/>
        </Button>

        <Collapse isOpened={relatedIsOpen}>
          {
            related.map ((res, i) => (
              <div key={`${id}-related-${i}`}
                   className="result">
                <h5>
                  <a href={res.link} target="_blank">
                    {res.header}
                  </a>
                </h5>
                <h6>{res.link}</h6>
                {renderHTML (res.info)}
                <div className="result-footer">
                  <Button onClick={this.props.onClickPromote}>
                    Promote
                  </Button>
                </div>
              </div>
            ))
          }
        </Collapse>
      </div>
    ) : null;
  };

  renderStrategyInfo = () => {
    const {toggleStrategyInfo, toggleRankInfo} = this;
    const {id, strategy, terms, search_terms, search_data, max_ranking, ranking} = this.props;
    const {stratInfoIsOpen, rankInfoIsOpen} = this.state;

    const config = {
      key: `${id}-strategy-info`,
      className: classnames ({
        'strategy-info': true,
        'open': stratInfoIsOpen,
      }),
    };

    const collapseClasses = {
      collapse: 'wrapper',
      content: 'details',
    };

    let _terms;
    if (typeof terms === `string`) {
      _terms = decodeURIComponent (terms.split (`+`).join (` `));
    } else if (Array.isArray (terms)) {
      _terms = decodeURIComponent (terms.join (` `));
    } else {
      _terms = `Search terms unavailable`;
    }


    return !!strategy && strategy !== `__manual__` ? (
      <div {...config}>
        Strategy:
        <Button type={`tag ${strategy}`}
                onClick={toggleStrategyInfo}>
          {strategy}
        </Button>

        <Collapse isOpened={stratInfoIsOpen}
                  theme={collapseClasses}>
          <span>Terms:</span>
          <p>{_terms}</p>
        </Collapse>
        {
          rankInfoIsOpen ? (
            <div {...config}>
              Ranking:
              <Button type={`tag ${strategy}`} onClick={toggleRankInfo}>
                {ranking ?
                ranking : null}
              </Button>
              <div {...config}>
                <div className={'block'}>
                  <p className={'strategy-info-margin inline'}>Method: </p>
                  <p className={'green-text strategy-info-margin inline'}>{
                    search_data ?
                    search_data['method'] : null
                  }</p>
                </div>
                <div className={'block'}>
                  <p className={'strategy-info-margin inline'}>Term Count: </p>
                  <p className={'green-text strategy-info-margin inline'}>{
                    search_data ?
                    search_data['method'] : null
                  }</p>
                </div>
              </div>
            </div>
          ) : (
            <div {...config}>
              Ranking:
              <Button type={`tag ${strategy}`} onClick={toggleRankInfo}>
                {ranking ?
                ranking : null}
              </Button>
            </div>
          )
        }
      </div>
    ) : null;
  };

  onClickPromote = () => {
    const {onClickPromote, link, selectMatch, strategy} = this.props;
    selectMatch(link, strategy);
    onClickPromote();
  };

  onClickExclude = () => {
    const {domain, exclude} = this.props;
    debug.log('excluding domain ', domain);
    exclude({domain});
    // alert.error(<p className={`alert-message`}>Domain '{domain}' added</p>)
  };

  render () {
    const {
      renderStrategyInfo,
      renderRelatedResults,
      onClickPromote,
      onClickExclude,
    } = this;

    const {info, link, header} = this.props;

    return (
      <div className="result result-selectable">
        <h5>
          <a target="_blank"
             href={link}>
            {header}
          </a>
        </h5>
        <h6>{link}</h6>
        <div className='exclude-agency-button'>
          <Button text onClick={onClickExclude}>
            Exclude domain from future searches
          </Button>
        </div>
        {renderHTML (info)}
        <br/>
        <div className="result-footer">
          {renderStrategyInfo ()}
          <Button onClick={onClickPromote}>
            Promote
          </Button>
          {renderRelatedResults ()}
        </div>
      </div>
    )
  }
}


/* ------------ redux connect ------------ */
const mapStateToProps = (state, ownProps) => {
  const jobSelector = createJobSelector(ownProps.id);
  const isLoading = createIsLoading(JobSearch.dependencies);
  const didError = createDidError(JobSearch.dependencies);
  const owpJobSelector = createOpenWebJobSelector(ownProps.id);

  return {
    didError: didError(state),
    isLoading: isLoading(state),
    job: owpJobSelector(state) || jobSelector(state),
  }
};

export default connect(mapStateToProps, {
  changeSearchTerms,
  createAgency,
})(JobSearch)

//
// export default compose(withAlert,
//   connect(mapStateToProps, {
//       changeSearchTerms,
//       createAgency,
//   })) (JobSearch);





// WEBPACK FOOTER //
// ./src/components/AgencyPostings/JobSearch.js